import React from 'react';
import { CdrPaginationControls } from './CdrPaginationControls';

interface CdrFilterBarProps {
  cdrsSearch: string;
  setCdrsSearch: (search: string) => void;
  searchArchive: boolean;
  setSearchArchive: (archive: boolean) => void;
  activeFilters: string[];
  setActiveFilters: (filters: string[]) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  cdrsPage: number;
  cdrsTotal: number;
  cdrsLoading: boolean;
  cdrsLastRefreshed: Date | null;
  isWsConnected: boolean;
  pendingNewCallsCount: number;
  setPendingNewCallsCount: (count: number) => void;
  loadCdrs: (page?: number, search?: string, sDate?: string, eDate?: string, filters?: string[], silent?: boolean, isArchive?: boolean) => void;
  setIsExplorerOpen: (open: boolean) => void;
  isLight: boolean;
  cdrsGroupsCount: number;
}

export function CdrFilterBar({
  cdrsSearch,
  setCdrsSearch,
  searchArchive,
  setSearchArchive,
  activeFilters,
  setActiveFilters,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  cdrsPage,
  cdrsTotal,
  cdrsLoading,
  cdrsLastRefreshed,
  isWsConnected,
  pendingNewCallsCount,
  setPendingNewCallsCount,
  loadCdrs,
  setIsExplorerOpen,
  isLight,
  cdrsGroupsCount
}: CdrFilterBarProps) {
  const textColor = isLight ? '#09090b' : 'white';
  const mutedColor = isLight ? '#6b7280' : '#a1a1aa';
  const borderColor = isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)';

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              placeholder={searchArchive ? "Search archive..." : "Search numbers or names..."}
              value={cdrsSearch}
              onChange={(e) => setCdrsSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadCdrs(0, cdrsSearch, startDate, endDate, activeFilters, false, searchArchive);
                }
              }}
              style={{
                width: '250px',
                padding: '0.75rem',
                paddingRight: cdrsSearch ? '2.2rem' : '0.75rem',
                background: isLight ? 'white' : 'rgba(0,0,0,0.4)',
                border: searchArchive ? (isLight ? '1px solid #0284c7' : '1px solid #0284c7') : `1px solid ${borderColor}`,
                color: textColor,
                borderRadius: '4px',
                outline: 'none'
              }}
            />
            {cdrsSearch && (
              <button
                type="button"
                onClick={() => {
                  setCdrsSearch('');
                  if (searchArchive) {
                    loadCdrs(0, '', startDate, endDate, activeFilters, false, true);
                  }
                }}
                title="Clear search"
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  background: 'transparent',
                  border: 'none',
                  color: mutedColor,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.2rem'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            )}
          </div>

          {searchArchive && (
            <button
              type="button"
              onClick={() => loadCdrs(0, cdrsSearch, startDate, endDate, activeFilters, false, true)}
              disabled={cdrsLoading}
              title={cdrsLoading ? "Searching cold archive..." : "Search cold archive"}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                padding: '0.7rem 1rem',
                borderRadius: '4px',
                border: '1px solid transparent',
                background: cdrsLoading
                  ? (isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)')
                  : (isLight ? '#0284c7' : '#0284c7'),
                color: cdrsLoading
                  ? (isLight ? '#64748b' : '#94a3b8')
                  : '#ffffff',
                cursor: cdrsLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                opacity: cdrsLoading ? 0.6 : 1,
                transition: 'all 0.2s',
                boxShadow: cdrsLoading ? 'none' : (isLight ? '0 1px 4px rgba(2, 132, 199, 0.3)' : '0 0 10px rgba(56, 189, 248, 0.3)')
              }}
            >
              {cdrsLoading ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                  <span>Search</span>
                </>
              )}
            </button>
          )}

          <div style={{ display: 'flex', background: isLight ? 'white' : 'rgba(0,0,0,0.4)', border: `1px solid ${borderColor}`, borderRadius: '4px', overflow: 'hidden' }}>
            {[
              { id: 'inbound', bg: isLight ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#9333ea" : "#a855f7"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 2 16 8 22 8"></polyline><line x1="22" y1="2" x2="16" y2="8"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>, title: 'Inbound' },
              { id: 'outbound', bg: isLight ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#2563eb" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 7 23 1 17 1"></polyline><line x1="16" y1="8" x2="23" y2="1"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>, title: 'Outbound' },
              { id: 'internal', bg: isLight ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#d97706" : "#eab308"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 11 21 7 17 3"></polyline><line x1="21" y1="7" x2="9" y2="7"></line><polyline points="7 21 3 17 7 13"></polyline><line x1="3" y1="17" x2="15" y2="17"></line></svg>, title: 'Internal' },
              { id: 'answered', bg: isLight ? 'rgba(52, 211, 153, 0.15)' : 'rgba(52, 211, 153, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#059669" : "#34d399"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>, title: 'Answered' },
              { id: 'missed', bg: isLight ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#dc2626" : "#f87171"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path><line x1="22" y1="2" x2="16" y2="8"></line><line x1="16" y1="2" x2="22" y2="8"></line></svg>, title: 'Missed' },
              { id: 'no-answer', bg: isLight ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#6b7280" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path><line x1="22" y1="2" x2="2" y2="22"></line></svg>, title: 'No Answer' },
              { id: 'system', bg: isLight ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#d97706" : "#eab308"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect><rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></svg>, title: 'System' },
              { id: 'hide-feature-codes', bg: isLight ? 'rgba(236, 72, 153, 0.15)' : 'rgba(236, 72, 153, 0.15)', icon: <svg style={{ width: '16px', height: '16px', minWidth: '16px', display: 'block' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#ec4899" : "#f472b6"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6v12M17.196 9L6.804 15M6.804 9l10.392 6"/></svg>, title: 'Hide Feature Codes' }
            ].map(filter => (
              <button
                key={filter.id}
                title={filter.title}
                onClick={() => {
                  const nextFilters = activeFilters.includes(filter.id)
                    ? activeFilters.filter(f => f !== filter.id)
                    : [...activeFilters, filter.id];
                  setActiveFilters(nextFilters);
                  if (searchArchive) {
                    loadCdrs(0, cdrsSearch, startDate, endDate, nextFilters, false, true);
                  }
                }}
                style={{
                  background: activeFilters.includes(filter.id) ? (isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)') : 'transparent',
                  color: textColor,
                  border: 'none',
                  borderRight: `1px solid ${borderColor}`,
                  padding: '0.4rem 0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: activeFilters.includes(filter.id) ? 1 : 0.4,
                  filter: activeFilters.includes(filter.id) ? 'none' : 'grayscale(100%)'
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '28px', height: '28px', borderRadius: '6px',
                  background: filter.bg
                }}>
                  {filter.icon}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: isLight ? 'white' : 'rgba(0,0,0,0.4)', border: `1px solid ${borderColor}`, borderRadius: '4px' }}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                const val = e.target.value;
                setEndDate(val);
                setStartDate('');
                const isOlderThanYear = val && (new Date(val).getTime() < Date.now() - 365 * 24 * 3600 * 1000);
                const shouldArchive = searchArchive || !!isOlderThanYear;
                if (shouldArchive !== searchArchive) {
                  setSearchArchive(shouldArchive);
                }
                loadCdrs(0, cdrsSearch, '', val, activeFilters, false, shouldArchive);
              }}
              title="Jump to Date"
              style={{ padding: '0.75rem', background: 'transparent', border: 'none', color: textColor, outline: 'none' }}
            />
            {endDate && (
              <button
                type="button"
                onClick={() => {
                  setEndDate('');
                  loadCdrs(0, cdrsSearch, '', '', activeFilters, false, searchArchive);
                }}
                title="Clear date filter"
                style={{ background: 'transparent', border: 'none', color: mutedColor, cursor: 'pointer', padding: '0 0.5rem', display: 'flex', alignItems: 'center' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              const next = !searchArchive;
              setSearchArchive(next);
              loadCdrs(0, cdrsSearch, startDate, endDate, activeFilters, false, next);
            }}
            title={searchArchive ? "Cold Archive search active (searching R2 historical storage). Click to switch to Hot Index (past 365 days)." : "Search historical cold archive (older than 365 days) in R2 storage"}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              padding: '0.55rem 0.85rem',
              borderRadius: '4px',
              border: searchArchive
                ? '1px solid #0284c7'
                : `1px solid ${borderColor}`,
              background: searchArchive
                ? (isLight ? '#0284c7' : 'rgba(56, 189, 248, 0.2)')
                : (isLight ? 'white' : 'rgba(0,0,0,0.4)'),
              color: searchArchive
                ? (isLight ? '#ffffff' : '#38bdf8')
                : (isLight ? '#4b5563' : '#a1a1aa'),
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.8rem',
              transition: 'all 0.2s',
              boxShadow: searchArchive ? (isLight ? '0 2px 8px rgba(2, 132, 199, 0.25)' : '0 0 12px rgba(56, 189, 248, 0.25)') : 'none'
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 8v13H3V8"></path><path d="M1 3h22v5H1z"></path><path d="M10 12h4"></path></svg>
            <span>Archive: {searchArchive ? 'ON' : 'OFF'}</span>
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {cdrsLastRefreshed && (
              <div style={{ fontSize: '0.75rem', color: mutedColor, marginBottom: '-0.25rem' }}>
                {searchArchive ? 'Searched' : 'Refreshed'}: {cdrsLastRefreshed.toLocaleTimeString()}
              </div>
            )}
            {!searchArchive ? (
              <div style={{ display: 'flex', background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.2)', borderRadius: '4px', border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                <button
                  onClick={() => loadCdrs(cdrsPage, cdrsSearch, startDate, endDate)}
                  title="Refresh now"
                  disabled={cdrsLoading}
                  style={{
                    background: 'transparent',
                    color: textColor,
                    border: 'none',
                    padding: '0.4rem 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: cdrsLoading ? 'not-allowed' : 'pointer',
                    borderRight: `1px solid ${borderColor}`,
                    outline: 'none',
                    transition: 'all 0.2s',
                    opacity: cdrsLoading ? 0.5 : 1
                  }}
                >
                  <svg style={{ width: '14px', height: '14px', minWidth: '14px', display: 'block' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                </button>

                <div
                  title={isWsConnected ? "Live real-time WebSocket updates active" : "Connecting to real-time stream..."}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.4rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: isWsConnected ? '#10b981' : mutedColor,
                    userSelect: 'none'
                  }}
                >
                  <span style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    backgroundColor: isWsConnected ? '#10b981' : '#f59e0b',
                    boxShadow: isWsConnected ? '0 0 6px rgba(16, 185, 129, 0.6)' : 'none',
                    display: 'inline-block'
                  }} />
                  Live
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)', borderRadius: '4px', border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                <button
                  onClick={() => loadCdrs(cdrsPage, cdrsSearch, startDate, endDate, activeFilters, false, true)}
                  title="Reload archive results"
                  disabled={cdrsLoading}
                  style={{
                    background: 'transparent',
                    color: textColor,
                    border: 'none',
                    padding: '0.4rem 0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: cdrsLoading ? 'not-allowed' : 'pointer',
                    borderRight: `1px solid ${borderColor}`,
                    outline: 'none',
                    opacity: cdrsLoading ? 0.5 : 1
                  }}
                >
                  <svg style={{ width: '13px', height: '13px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0.35rem 0.65rem', gap: '0.35rem', color: mutedColor, fontSize: '0.75rem', userSelect: 'none' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                  <span>Auto-refresh disabled</span>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsExplorerOpen(true)}
            style={{ background: 'transparent', border: 'none', color: textColor, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', opacity: 0.8, transition: 'opacity 0.2s' }}
            title="Explorer"
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
          >
            <svg style={{ width: '18px', height: '18px', minWidth: '18px', display: 'block' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Explorer</span>
          </button>
        </div>
        <CdrPaginationControls
          cdrsPage={cdrsPage}
          cdrsTotal={cdrsTotal}
          cdrsGroupsCount={cdrsGroupsCount}
          loadCdrs={loadCdrs}
          isLight={isLight}
          isBottom={false}
          searchArchive={searchArchive}
        />
      </div>

      {pendingNewCallsCount > 0 && !searchArchive && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem', position: 'sticky', top: '0.5rem', zIndex: 30 }}>
          <button
            onClick={() => {
              setPendingNewCallsCount(0);
              setCdrsSearch('');
              setStartDate('');
              setEndDate('');
              loadCdrs(0, '', '', '', activeFilters);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              background: '#2563eb',
              color: 'white',
              padding: '0.5rem 1.25rem',
              borderRadius: '9999px',
              fontWeight: 600,
              fontSize: '0.85rem',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(37, 99, 235, 0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(37, 99, 235, 0.4)'; }}
          >
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 8px #4ade80' }} />
            {pendingNewCallsCount} new call{pendingNewCallsCount > 1 ? 's' : ''} received (Click to view)
          </button>
        </div>
      )}
    </>
  );
}
